import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ViewChild, TemplateRef } from '@angular/core';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

/**
 * Vendor-Backed Receivables — accounting view of open supptrans credits
 * from prior vendor-backed AR receipts. Shows per-vendor KPI tiles + a
 * drill-down table. Apply-deposit modal lets accounting clear N credits
 * with one bank deposit.
 */
@Component({
	selector: 'app-vendor-backed-balances',
	templateUrl: './vendor-backed-balances.component.html',
	styleUrls: ['./vendor-backed-balances.component.scss']
})
export class VendorBackedBalancesComponent implements OnInit {
	@ViewChild('applyDialog', { static: true }) applyDialog!: TemplateRef<any>;

	loading = false;
	loadingDetail = false;
	applying = false;

	vendors: any[] = [];
	totals = { open_balance: 0, open_invoices: 0 };

	// Drill state
	selectedVendor: any = null;
	detailRows: any[] = [];
	selectedIds: Record<number, boolean> = {};

	applyForm: UntypedFormGroup;
	bankaccounts: any[] = [];

	constructor(
		private paymentsService: PaymentsService,
		private globalSearchService: GlobalSearchService,
		private fb: UntypedFormBuilder,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.applyForm = this.fb.group({
			deposit_amount: [0, Validators.required],
			bankaccount: ['', Validators.required],
			postdate: [new Date().toISOString().substring(0, 10), Validators.required],
		});
		this.globalSearchService.bankaccounts.subscribe(b => { this.bankaccounts = b || []; });
		this.load();
	}

	load() {
		this.loading = true;
		this.paymentsService.getVendorBackedBalances().subscribe((r: any) => {
			this.loading = false;
			if (r?.success) {
				this.vendors = r.vendors || [];
				this.totals = {
					open_balance: this.vendors.reduce((a, v) => a + Number(v.open_balance || 0), 0),
					open_invoices: this.vendors.reduce((a, v) => a + Number(v.open_invoices || 0), 0),
				};
			}
		});
	}

	openDetail(vendor: any) {
		this.selectedVendor = vendor;
		this.detailRows = [];
		this.selectedIds = {};
		this.loadingDetail = true;
		this.paymentsService.getVendorBackedDetail(vendor.supplierid).subscribe((r: any) => {
			this.loadingDetail = false;
			if (r?.success) {
				this.detailRows = r.rows || [];
			}
		});
	}

	closeDetail() {
		this.selectedVendor = null;
		this.detailRows = [];
		this.selectedIds = {};
	}

	get selectedTotal(): number {
		return this.detailRows
			.filter(r => this.selectedIds[r.id])
			.reduce((a, r) => a + Number(r.open_balance || 0), 0);
	}

	get selectedCount(): number {
		return Object.values(this.selectedIds).filter(Boolean).length;
	}

	openApply() {
		if (!this.selectedCount) {
			alert('Pick at least one open credit to apply against');
			return;
		}
		this.applyForm.get('deposit_amount').setValue(this.selectedTotal);
		this.dialog.open(this.applyDialog, { width: '560px' });
	}

	postApply() {
		if (!this.applyForm.valid) { return; }
		this.applying = true;
		const payload = {
			vendor_id: this.selectedVendor.supplierid,
			bankaccount: this.applyForm.get('bankaccount').value,
			postdate: this.applyForm.get('postdate').value,
			deposit_amount: this.applyForm.get('deposit_amount').value,
			supptrans_ids: Object.keys(this.selectedIds).filter(k => this.selectedIds[+k]).map(Number),
		};
		this.paymentsService.applyVendorBackedDeposit(payload).subscribe((r: any) => {
			this.applying = false;
			if (r?.success) {
				this.globalSearchService.showNotification(
					`Applied ${r.applied?.length || 0} credits (${r.reference})`, 'success', 'bottom', 'left'
				);
				this.dialog.closeAll();
				this.openDetail(this.selectedVendor);
				this.load();
			} else {
				alert(r?.message || 'Apply failed');
			}
		});
	}
}
